/* HC custom — blog hub tabs. Panels are server-rendered by the tab blocks;
   this builds the tab bar from each panel's label and runs the standard
   tablist keyboard contract (click or Enter/Space activates; Left/Right/
   Home/End move and activate; roving tabindex keeps one tab stop). */

class HcBlogHub extends HTMLElement {
  connectedCallback() {
    this.#build();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    document.addEventListener('shopify:section:load', this.#handleSectionLoad);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
  }

  get #tabs() {
    return [...this.querySelectorAll('[role="tab"]')];
  }

  get #panels() {
    return [...this.querySelectorAll('[role="tabpanel"]')];
  }

  #build() {
    const tablist = this.querySelector('[role="tablist"]');
    const panels = this.#panels;
    if (!tablist || !panels.length) return;

    const prefix = tablist.dataset.tabsPrefix || 'hc-bh';
    tablist.textContent = '';
    panels.forEach((panel, i) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'hc-blog-hub__tab';
      tab.setAttribute('role', 'tab');
      tab.id = `${prefix}-tab-${i}`;
      panel.id = panel.id || `${prefix}-panel-${i}`;
      tab.setAttribute('aria-controls', panel.id);
      panel.setAttribute('aria-labelledby', tab.id);

      const label = document.createElement('span');
      label.textContent = panel.dataset.tabLabel || `Tab ${i + 1}`;
      tab.appendChild(label);

      // The count comes from the panel because block settings aren't readable
      // from the section that draws the tab bar.
      const count = panel.dataset.count;
      if (tablist.hasAttribute('data-show-counts') && count && count !== '0') {
        const badge = document.createElement('span');
        badge.className = 'hc-blog-hub__tab-count';
        badge.textContent = count;
        tab.appendChild(badge);
      }

      tablist.appendChild(tab);
    });

    this.#activate(0);
    this.setAttribute('data-ready', '');
  }

  /** @param {number} index */
  #activate(index) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (panels[i]) panels[i].hidden = !selected;
    });
  }

  #handleClick = (/** @type {MouseEvent} */ event) => {
    const tab = /** @type {HTMLElement} */ (event.target).closest?.('[role="tab"]');
    if (!tab || !this.contains(tab)) return;
    this.#activate(this.#tabs.indexOf(tab));
  };

  #handleKeydown = (/** @type {KeyboardEvent} */ event) => {
    const tab = /** @type {HTMLElement} */ (event.target).closest?.('[role="tab"]');
    if (!tab || !this.contains(tab)) return;
    const tabs = this.#tabs;
    const current = tabs.indexOf(tab);
    let next = null;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    event.preventDefault();
    this.#activate(next);
    /** @type {HTMLElement} */ (tabs[next])?.focus();
  };

  #handleBlockSelect = (/** @type {Event & { target: HTMLElement }} */ event) => {
    const panel = event.target?.closest?.('[role="tabpanel"]');
    if (!panel || !this.contains(panel)) return;
    this.#activate(this.#panels.indexOf(panel));
  };

  #handleSectionLoad = (/** @type {Event & { target: HTMLElement }} */ event) => {
    if (event.target?.contains?.(this)) this.#build();
  };
}

if (!customElements.get('hc-blog-hub')) {
  customElements.define('hc-blog-hub', HcBlogHub);
}
